import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { canDecide, canEditSchedule } from "../lib/roles";
import { recordBetaSignal } from "../lib/betaTelemetry";
import DecisionOutcomePanel from "../features/autonomy/DecisionOutcomePanel";
import AutonomyBoundaryPanel from "../features/autonomy/AutonomyBoundaryPanel";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  Activity,
  AlertTriangle,
  CalendarClock,
  CheckCircle2,
  ClipboardCheck,
  Info,
  Package,
  RefreshCw,
  Radar,
  Truck,
  XCircle,
} from "lucide-react";

const formatDate = (value) =>
  value ? new Date(value).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—";

const signed = (value) => (value > 0 ? `+${value}` : `${value}`);

/**
 * Consequence-ranked exception queue. Severity tiers, not chronology:
 * 0 milestone breaches (by slip), 1 stale/negative float, 2 blocked critical
 * work, 3 constraint-driven work, 4 inputs excluded from the calculation.
 */
const buildExceptions = (analysis, proposals, navigate, projectId) => {
  if (!analysis || analysis.empty) return [];
  const activities = analysis.activities || [];
  const out = [];

  (analysis.milestoneViolations || []).forEach((v) => {
    out.push({
      severity: 0,
      key: `milestone-${v.activityId}`,
      text: `Contract milestone "${v.name}" forecast ${v.slipDays} day(s) past ${formatDate(v.requiredFinishBy)}.`,
      action: { label: "Gantt", onClick: () => navigate(`/projects/${projectId}/gantt`) },
      rank: -(v.slipDays || 0),
    });
  });

  proposals
    .filter((p) => p.state === "PROPOSED")
    .forEach((p) => {
      out.push({
        severity: 1,
        key: `proposal-${p.proposalId}`,
        text: `A ${(p.proposalKind || "recovery").toLowerCase()} proposal (${p.trigger || "slip"}) is waiting on a decision.`,
        rank: 0,
      });
    });

  activities
    .filter((a) => a.totalFloatDays < 0 && !a.complete)
    .sort((a, b) => a.totalFloatDays - b.totalFloatDays)
    .forEach((a) => {
      out.push({
        severity: 1,
        key: `float-${a.activityInstanceId}`,
        text: `${a.name} has ${a.totalFloatDays} day(s) of float — it cannot make its date as sequenced.`,
        rank: a.totalFloatDays,
      });
    });

  activities
    .filter((a) => a.constraintDriven && a.critical && !a.complete)
    .forEach((a) => {
      out.push({
        severity: 2,
        key: `crit-constraint-${a.activityInstanceId}`,
        text: `${a.name} is critical and held by a ${a.drivingConstraint?.source || "constraint"}: ${
          a.drivingConstraint?.detail || a.drivingConstraint?.name || "see analysis"
        }`,
        rank: 0,
      });
    });

  activities
    .filter((a) => a.constraintDriven && !a.critical && !a.complete)
    .forEach((a) => {
      out.push({
        severity: 3,
        key: `constraint-${a.activityInstanceId}`,
        text: `${a.name} is held by a ${a.drivingConstraint?.source || "constraint"} (float ${a.totalFloatDays}d).`,
        rank: a.totalFloatDays ?? 0,
      });
    });

  (analysis.rejected || []).forEach((item) => {
    out.push({
      severity: 4,
      key: `rejected-${item.kind}-${item.itemId}`,
      text: `Excluded from the calculation — ${item.kind} ${item.itemId}: ${item.reason}`,
      rank: 0,
    });
  });

  return out.sort((a, b) => a.severity - b.severity || a.rank - b.rank);
};

/**
 * Scheduling Control Tower.
 *
 * One surface for execution truth: schedule health, the critical path, float,
 * blockers, and the proposed recovery a human is being asked to approve.
 * Everything here is read-only until an operator explicitly approves or
 * rejects a proposal.
 */
const ControlTowerPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState("");
  const [analysis, setAnalysis] = useState(null);
  const [analyzedAt, setAnalyzedAt] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [activeProposal, setActiveProposal] = useState(null);
  const [decisionOutcome, setDecisionOutcome] = useState(null);
  const [loading, setLoading] = useState(true);
  const [featureOff, setFeatureOff] = useState(false);
  const [deciding, setDeciding] = useState(false);
  // Drops analyze responses that arrive after the user switched projects.
  const analyzeSeqRef = useRef(0);

  useEffect(() => {
    fetchProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedProject) {
      fetchAnalysis();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject]);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      const loaded = response.data || [];
      setProjects(loaded);
      const requested = searchParams.get("project_id");
      if (requested && loaded.some((p) => p.id === requested)) {
        setSelectedProject(requested);
      } else if (loaded.length > 0) {
        setSelectedProject(loaded[0].id);
      }
    } catch (error) {
      toast.error("Failed to load projects");
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalysis = async () => {
    const seq = ++analyzeSeqRef.current;
    setLoading(true);
    setFeatureOff(false);
    setAnalysis(null);
    setActiveProposal(null);
    try {
      const [analysisRes, proposalsRes] = await Promise.all([
        axios.post(`${API}/scheduling-agent/projects/${selectedProject}/analyze`),
        axios.get(`${API}/scheduling-agent/projects/${selectedProject}/proposals`),
      ]);
      if (seq !== analyzeSeqRef.current) return; // a newer project was selected
      setAnalysis(analysisRes.data);
      setAnalyzedAt(new Date());
      setProposals(proposalsRes.data || []);
    } catch (error) {
      if (seq !== analyzeSeqRef.current) return;
      const status = error?.response?.status;
      if (status === 503) {
        setFeatureOff(true);
      } else if (status === 409) {
        setAnalysis({ empty: true });
      } else {
        toast.error("Failed to load the execution schedule");
      }
      setProposals([]);
    } finally {
      if (seq === analyzeSeqRef.current) setLoading(false);
    }
  };

  const openProposal = async (proposalId) => {
    try {
      const response = await axios.get(
        `${API}/scheduling-agent/projects/${selectedProject}/proposals/${proposalId}`
      );
      setActiveProposal(response.data);
    } catch (error) {
      toast.error("Failed to load the proposal");
    }
  };

  const [proposing, setProposing] = useState(false);

  /**
   * Ask the scheduling agent to calculate a recovery proposal for the
   * current situation. Calculation only — dates move nowhere without the
   * explicit approval flow below.
   */
  const proposeRecovery = async () => {
    setProposing(true);
    try {
      const response = await axios.post(
        `${API}/scheduling-agent/projects/${selectedProject}/resequence-proposal`,
        { contractVersion: "sa-phase-2", trigger: "MATERIAL_DELAY" }
      );
      toast.success("Recovery proposal calculated — review it below before deciding.");
      setActiveProposal(response.data);
      const proposalsRes = await axios.get(
        `${API}/scheduling-agent/projects/${selectedProject}/proposals`
      );
      setProposals(proposalsRes.data || []);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(detail?.message || "Could not calculate a recovery proposal");
    } finally {
      setProposing(false);
    }
  };

  const decide = async (decision) => {
    if (!activeProposal) return;
    setDeciding(true);
    setDecisionOutcome(null);
    try {
      const response = await axios.post(
        `${API}/scheduling-agent/projects/${selectedProject}/proposals/decision`,
        {
          contractVersion: "sa-phase-2",
          proposalId: activeProposal.proposalId,
          decision,
          confirmed: true,
        }
      );
      // The response states applied:false, an applied count of zero, and
      // every table it declined to write. A toast auto-dismisses all of
      // that, and "recorded, nothing moved" becomes "done" in a reader's
      // head. It is kept on screen until the reader dismisses it.
      setDecisionOutcome(response.data);
      setActiveProposal(null);
      fetchAnalysis();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (detail?.code === "stale_proposal") {
        recordBetaSignal("stale-proposal", { proposalId: activeProposal?.proposalId });
      }
      toast.error(detail?.message || "The decision could not be recorded");
    } finally {
      setDeciding(false);
    }
  };

  const activities = analysis?.activities || [];
  const criticalCount = activities.filter((a) => a.critical).length;
  const constrainedCount = activities.filter((a) => a.constraintDriven).length;
  const negativeFloat = activities.filter((a) => a.totalFloatDays < 0).length;
  // Consequence-ordered look-ahead: critical first, then least float, then
  // soonest start — an activity that cannot slip outranks one that merely
  // starts tomorrow.
  const lookahead = activities
    .filter((a) => !a.complete)
    .slice()
    .sort(
      (a, b) =>
        (b.critical === true) - (a.critical === true) ||
        (a.totalFloatDays ?? 0) - (b.totalFloatDays ?? 0) ||
        new Date(a.earlyStart) - new Date(b.earlyStart)
    )
    .slice(0, 12);
  const openProposals = proposals.filter((p) => p.state === "PROPOSED");
  const payload = activeProposal?.payload || {};
  const exceptions = buildExceptions(analysis, proposals, navigate, selectedProject);
  const userCanDecide = canDecide(user);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Scheduling Control Tower</h1>
          <p className="text-sm text-muted-foreground">
            Execution truth: readiness, constraints, critical path, and proposed recovery.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={selectedProject} onValueChange={setSelectedProject}>
            <SelectTrigger className="w-72">
              <SelectValue placeholder="Select a project" />
            </SelectTrigger>
            <SelectContent>
              {projects.map((project) => (
                <SelectItem key={project.id} value={project.id}>
                  {project.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetchAnalysis} disabled={!selectedProject || loading} title="Re-run the execution analysis">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>
      {analyzedAt && !loading && (
        <p className="text-xs text-muted-foreground -mt-4">
          Analysis run {analyzedAt.toLocaleTimeString()} — read-only; it changes no schedule dates.
        </p>
      )}

      {featureOff && (
        <Card>
          <CardContent className="flex items-start gap-3 py-6">
            <Info className="mt-0.5 h-5 w-5 text-muted-foreground" />
            <div>
              <p className="font-medium">The Scheduling Agent is not enabled</p>
              <p className="text-sm text-muted-foreground">
                Ask an administrator to enable the Scheduling Agent Phase 2 feature flags for this
                environment.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {analysis?.empty && (
        <Card>
          <CardContent className="flex items-start gap-3 py-6">
            <Info className="mt-0.5 h-5 w-5 text-muted-foreground" />
            <div>
              <p className="font-medium">No execution schedule yet</p>
              <p className="text-sm text-muted-foreground">
                This project has no execution activity instances. Build them from the cost code,
                variant, and location before analyzing.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {analysis && !analysis.empty && (
        <>
          <div className="grid gap-4 md:grid-cols-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Forecast finish
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-semibold">{formatDate(analysis.projectFinish)}</p>
                <p className="text-xs text-muted-foreground">
                  from {formatDate(analysis.projectStart)}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Critical activities
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-semibold">{criticalCount}</p>
                <p className="text-xs text-muted-foreground">of {activities.length} total</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Constraint-driven
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-semibold">{constrainedCount}</p>
                <p className="text-xs text-muted-foreground">moved by material, permit, or access</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Negative float
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className={`text-2xl font-semibold ${negativeFloat ? "text-red-600" : ""}`}>
                  {negativeFloat}
                </p>
                <p className="text-xs text-muted-foreground">activities that cannot make their date</p>
              </CardContent>
            </Card>
          </div>

          {exceptions.length > 0 && (
            <Card className="border-border">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between gap-2">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Radar className="h-4 w-4 text-primary" />
                    Exceptions — ranked by consequence
                  </CardTitle>
                  {canEditSchedule(user) && openProposals.length === 0 && (
                    <Button variant="outline" size="sm" onClick={proposeRecovery} disabled={proposing}>
                      {proposing ? "Calculating…" : "Propose recovery"}
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-1.5">
                {exceptions.slice(0, 12).map((ex) => (
                  <div key={ex.key} className="flex items-center justify-between gap-2 rounded-md bg-muted/30 px-3 py-2 text-sm">
                    <span className={ex.severity === 0 ? "text-red-500 font-medium" : ex.severity === 1 ? "text-red-400" : ex.severity === 2 ? "text-amber-400" : "text-muted-foreground"}>
                      {ex.text}
                    </span>
                    {ex.action && (
                      <Button variant="ghost" size="sm" className="h-6 px-2 text-xs shrink-0" onClick={ex.action.onClick}>
                        {ex.action.label}
                      </Button>
                    )}
                  </div>
                ))}
                {exceptions.length > 12 && (
                  <p className="text-xs text-muted-foreground">+{exceptions.length - 12} lower-consequence items below in the detail cards.</p>
                )}
              </CardContent>
            </Card>
          )}

          {analysis.milestoneViolations?.length > 0 && (
            <Card className="border-red-200">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                  Contract milestones at risk
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {analysis.milestoneViolations.map((violation) => (
                  <div key={violation.activityId} className="flex items-center justify-between text-sm">
                    <span className="font-medium">{violation.name}</span>
                    <span className="text-red-600">
                      {formatDate(violation.forecastFinish)} — {violation.slipDays} day(s) past{" "}
                      {formatDate(violation.requiredFinishBy)}
                    </span>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {openProposals.length > 0 && (
            <Card className="border-amber-200">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <ClipboardCheck className="h-4 w-4 text-amber-600" />
                  Proposed recovery awaiting your decision
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {openProposals.map((proposal) => (
                  <div
                    key={proposal.proposalId}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-md border p-3"
                  >
                    <div>
                      <p className="text-sm font-medium">{proposal.trigger}</p>
                      <p className="text-xs text-muted-foreground">
                        Confidence {proposal.confidence || "unknown"} · proposed{" "}
                        {formatDate(proposal.createdAt)}
                      </p>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => openProposal(proposal.proposalId)}>
                      Review before / after
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {decisionOutcome && (
            <DecisionOutcomePanel
              response={decisionOutcome}
              onDismiss={() => setDecisionOutcome(null)}
            />
          )}

          {activeProposal && (
            <Card className="border-blue-200">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Activity className="h-4 w-4 text-blue-600" />
                  Proposal {activeProposal.proposalId.slice(0, 12)}…
                  {activeProposal.stale && (
                    <Badge variant="outline" className="border-red-200 bg-red-50 text-red-700">
                      Stale — the schedule changed
                    </Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-3 text-sm md:grid-cols-3">
                  <div>
                    <p className="text-muted-foreground">Baseline finish</p>
                    <p className="font-medium">{formatDate(payload.baselineFinish)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Proposed finish</p>
                    <p className="font-medium">{formatDate(payload.proposedFinish)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Project slip</p>
                    <p className="font-medium">
                      {payload.projectSlipDays != null ? `${payload.projectSlipDays} day(s)` : "—"}
                    </p>
                  </div>
                </div>

                {payload.costExposure && (
                  <div className="rounded-md bg-muted/40 p-3 text-sm">
                    <p className="font-medium">
                      Cost exposure: {payload.costExposure.currency}{" "}
                      {Number(payload.costExposure.impactAmount).toLocaleString()}
                    </p>
                    <p className="text-xs text-muted-foreground">{payload.costExposure.formula}</p>
                  </div>
                )}

                {payload.movements?.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-left text-xs uppercase text-muted-foreground">
                          <th className="py-2 pr-4">Activity</th>
                          <th className="py-2 pr-4">Was</th>
                          <th className="py-2 pr-4">Proposed</th>
                          <th className="py-2 pr-4">Δ</th>
                          <th className="py-2">Why it moved</th>
                        </tr>
                      </thead>
                      <tbody>
                        {payload.movements.map((movement) => (
                          <tr key={movement.activityId} className="border-b last:border-0">
                            <td className="py-2 pr-4 font-medium">{movement.name}</td>
                            <td className="py-2 pr-4 text-muted-foreground">
                              {formatDate(movement.previousStart)}
                            </td>
                            <td className="py-2 pr-4">{formatDate(movement.proposedStart)}</td>
                            <td className="py-2 pr-4">
                              {movement.startDeltaDays != null ? signed(movement.startDeltaDays) : "—"}
                            </td>
                            <td className="py-2 text-muted-foreground">{movement.reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {payload.alternatives?.length > 0 && (
                  <div>
                    <p className="mb-1 text-sm font-medium">Alternatives considered</p>
                    <ul className="list-inside list-disc text-sm text-muted-foreground">
                      {payload.alternatives.map((alternative, index) => (
                        <li key={index}>{alternative.summary}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {payload.missingInformation?.length > 0 && (
                  <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
                    <p className="mb-1 text-sm font-medium text-amber-900">Missing information</p>
                    <ul className="list-inside list-disc text-sm text-amber-800">
                      {payload.missingInformation.map((note, index) => (
                        <li key={index}>{note}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="flex flex-wrap items-center gap-2 border-t pt-4">
                  {userCanDecide ? (
                    <>
                      <Button
                        onClick={() => decide("APPROVE")}
                        disabled={deciding || activeProposal.stale}
                        title={activeProposal.stale ? "The schedule changed since this proposal was calculated — re-run the analysis to get a current proposal." : undefined}
                      >
                        <CheckCircle2 className="mr-2 h-4 w-4" />
                        Approve proposal
                      </Button>
                      <Button variant="outline" onClick={() => decide("REJECT")} disabled={deciding}>
                        <XCircle className="mr-2 h-4 w-4" />
                        Reject
                      </Button>
                    </>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      Recovery decisions are reserved for project managers and admins. You can review
                      everything here.
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Approving records the decision. No planned date changes until the reviewed apply
                    path is enabled.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <CalendarClock className="h-4 w-4" />
                Look-ahead
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {lookahead.length === 0 && (
                <p className="text-sm text-muted-foreground">No remaining activities.</p>
              )}
              {lookahead.map((activity) => (
                <div
                  key={activity.activityInstanceId}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-md border p-3"
                >
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{activity.name}</span>
                      {activity.critical && (
                        <Badge variant="outline" className="border-red-200 bg-red-50 text-red-700">
                          Critical
                        </Badge>
                      )}
                      {activity.constraintDriven && activity.drivingConstraint && (
                        <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-800">
                          {activity.drivingConstraint.source === "material" ? (
                            <Package className="mr-1 h-3 w-3" />
                          ) : (
                            <Truck className="mr-1 h-3 w-3" />
                          )}
                          {activity.drivingConstraint.name}
                        </Badge>
                      )}
                      {activity.locked && <Badge variant="outline">Locked</Badge>}
                    </div>
                    {activity.warnings?.length > 0 && (
                      <p className="mt-1 text-xs text-muted-foreground">{activity.warnings[0]}</p>
                    )}
                  </div>
                  <div className="text-right text-sm">
                    <p>
                      {formatDate(activity.earlyStart)} → {formatDate(activity.earlyFinish)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      float {activity.totalFloatDays} d · {activity.durationDays} d duration
                    </p>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {analysis.rejected?.length > 0 && (
            <Card className="border-amber-200">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  Excluded from the calculation
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-1 text-sm text-muted-foreground">
                {analysis.rejected.map((item) => (
                  <p key={`${item.kind}-${item.itemId}`}>
                    <span className="font-medium">{item.kind}</span> {item.itemId}: {item.reason}
                  </p>
                ))}
              </CardContent>
            </Card>
          )}
        </>
      )}

      {loading && <p className="text-sm text-muted-foreground">Loading…</p>}

      {/* The boundary of machine authority, stated where a person
          governing a decision can read it. Not a per-project mode:
          this platform has no autonomy-level field to read one from. */}
      <AutonomyBoundaryPanel />
    </div>
  );
};

export default ControlTowerPage;
